import { BUILTIN_KERNEL_IDS } from "./builtins/bundledArtifactIds.js";
import { readRawCanvasTemplateBucket } from "./canvasTemplateStore.js";
import { readRawKernelBucket } from "./kernelStore.js";
import { createTenantViewRequestId } from "./tenantViewIds.js";
import { inventoryDefinitionSecrets } from "./tenantViewSecretDetector.js";
import { tenantViewSecretOverrideDigest } from "./tenantViewSecretOverrides.js";
import { openTenantViewWriteJournal } from "./tenantViewWriteJournal.js";
import {
    browserStorageOwnerIsCurrent,
    requireBrowserStorageOwner,
} from "./browserStorageScope.js";

const MAPPING_KEY_PREFIX = "originDashboard.tenantViewLegacyImport.v1";
const LEGACY_PIN_KEY_PREFIX = "originDashboard.kernelPins.v1";
const MIGRATED_PIN_KEY_PREFIX = "originDashboard.kernelPins.v2";

const KERNEL_KIND = "TENANT_VIEW_KIND_KERNEL";
const CANVAS_KIND = "TENANT_VIEW_KIND_CANVAS";
const TEXT_ENCODER = new TextEncoder();

class LegacyImportIdentityChangedError extends Error {}

function encodeKeyPart(value) {
    return encodeURIComponent(String(value));
}

function requireIdentity(value, label) {
    if (value == null || String(value).trim() === "") {
        throw new TypeError(`${label} is required`);
    }
    return String(value);
}

function mappingStorageKey({ tenantId, userId, bucketKey, localRecordId }) {
    return [
        MAPPING_KEY_PREFIX,
        encodeKeyPart(requireIdentity(tenantId, "tenantId")),
        encodeKeyPart(requireIdentity(userId, "userId")),
        encodeKeyPart(requireIdentity(bucketKey, "bucketKey")),
        encodeKeyPart(requireIdentity(localRecordId, "localRecordId")),
    ].join(":");
}

function migratedPinStorageKey(tenantId, userId) {
    return [
        MIGRATED_PIN_KEY_PREFIX,
        encodeKeyPart(requireIdentity(tenantId, "tenantId")),
        encodeKeyPart(requireIdentity(userId, "userId")),
    ].join(":");
}

function readJson(storage, key) {
    let raw;
    try {
        raw = storage.getItem(key);
    } catch (error) {
        return { status: "unavailable", key, error };
    }
    if (raw === null) return { status: "absent", key };
    try {
        return { status: "ok", key, value: JSON.parse(raw) };
    } catch (error) {
        return { status: "invalid", key, error };
    }
}

function writeJson(storage, key, value) {
    storage.setItem(key, JSON.stringify(value));
}

function isPlainObject(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value);
}

function isMapping(value) {
    if (
        !isPlainObject(value)
        || !["pending", "complete", "rejected"].includes(value.state)
    ) {
        return false;
    }
    if (typeof value.createRequestId !== "string" || value.createRequestId.length === 0) return false;
    if (value.state === "pending" || value.state === "rejected") {
        const requestShape = typeof value.name === "string"
            && typeof value.title === "string"
            && Number.isInteger(value.formatVersion)
            && typeof value.definitionJson === "string"
            && (
                value.secretOverrides === undefined
                || Array.isArray(value.secretOverrides)
            );
        if (!requestShape) return false;
        return value.state === "pending"
            || typeof value.rejectionMessage === "string";
    }
    return typeof value.serverId === "string"
        && value.serverId.length > 0
        && ["string", "number"].includes(typeof value.serverVersion);
}

export function readLegacyViewMapping(
    { tenantId, userId, bucketKey, localRecordId },
    storage = globalThis.localStorage,
) {
    const key = mappingStorageKey({ tenantId, userId, bucketKey, localRecordId });
    const result = readJson(storage, key);
    if (result.status !== "ok") return result;
    if (!isMapping(result.value)) {
        return {
            status: "invalid",
            key,
            error: new TypeError("legacy import mapping is malformed"),
        };
    }
    return { status: "ok", key, mapping: result.value };
}

export function readRawLegacyKernelPinMap(
    bucketId = "default",
    storage = globalThis.localStorage,
) {
    const id = bucketId == null || bucketId === "" ? "default" : String(bucketId);
    const key = `${LEGACY_PIN_KEY_PREFIX}:${id}`;
    try {
        storage.removeItem(key);
        return { status: "absent", key, records: null };
    } catch (error) {
        return { status: "unavailable", key, records: null, error };
    }
}

export function readMigratedPinMap(
    tenantId,
    userId,
    storage = globalThis.localStorage,
) {
    const key = migratedPinStorageKey(tenantId, userId);
    const result = readJson(storage, key);
    if (result.status !== "ok") return result;
    if (!isPlainObject(result.value)) {
        return {
            status: "invalid",
            key,
            error: new TypeError("migrated kernel pins must contain a JSON object"),
        };
    }
    return { status: "ok", key, map: result.value };
}

export function writeMigratedPinMap(
    tenantId,
    userId,
    map,
    storage = globalThis.localStorage,
) {
    if (!isPlainObject(map)) throw new TypeError("migrated kernel pins must be an object");
    const key = migratedPinStorageKey(tenantId, userId);
    writeJson(storage, key, map);
    return key;
}

export function remapLegacyKernelPinMap(pinMap, resolveCommand) {
    if (!isPlainObject(pinMap)) {
        return {
            status: "invalid",
            map: null,
            unresolved: [],
            error: new TypeError("legacy kernel pins must be an object"),
        };
    }
    if (typeof resolveCommand !== "function") {
        throw new TypeError("resolveCommand must be a function");
    }

    const map = {};
    const unresolved = [];
    for (const [scopeKey, commands] of Object.entries(pinMap)) {
        if (!Array.isArray(commands) || commands.some((command) => typeof command !== "string")) {
            return {
                status: "invalid",
                map: null,
                unresolved,
                error: new TypeError(`legacy kernel pins for ${scopeKey} must be strings`),
            };
        }
        const ids = [];
        for (const command of commands) {
            const id = resolveCommand(command);
            if (typeof id !== "string" || id.length === 0) {
                unresolved.push({ scopeKey, command });
                continue;
            }
            if (!ids.includes(id)) ids.push(id);
        }
        if (ids.length > 0) map[scopeKey] = ids;
    }
    return unresolved.length > 0
        ? { status: "incomplete", map: null, unresolved }
        : { status: "complete", map, unresolved: [] };
}

function cloneDefinition(record, kind) {
    const definition = JSON.parse(JSON.stringify(record));
    delete definition.id;
    delete definition.builtin;
    delete definition.createdAt;
    delete definition.updatedAt;
    delete definition.formatVersion;
    delete definition.format_version;
    delete definition.title;
    if (kind === "kernel") delete definition.command;
    else delete definition.slug;
    return definition;
}

function canvasCells(definition, formatVersion) {
    if (formatVersion === 1) {
        if (!Array.isArray(definition.rows)) {
            throw new TypeError("format 1 canvas must contain rows");
        }
        return definition.rows.flatMap((row) => {
            if (!isPlainObject(row) || !Array.isArray(row.cells)) {
                throw new TypeError("canvas rows must contain cells arrays");
            }
            return row.cells;
        });
    }
    if (!Array.isArray(definition.items)) {
        throw new TypeError("format 0 canvas must contain items");
    }
    return definition.items;
}

function rewriteCanvasDependencies(record, resolveCommand) {
    const definition = cloneDefinition(record, "canvas");
    const formatVersion = Array.isArray(record.rows) ? 1 : 0;
    const unresolved = [];
    for (const cell of canvasCells(definition, formatVersion)) {
        if (!isPlainObject(cell) || typeof cell.command !== "string" || cell.command.length === 0) {
            throw new TypeError("canvas cells must contain a command");
        }
        const kernelId = resolveCommand(cell.command);
        if (typeof kernelId !== "string" || kernelId.length === 0) {
            unresolved.push(cell.command);
            continue;
        }
        cell.kernel_id = kernelId;
    }
    return {
        definition,
        formatVersion,
        unresolved: [...new Set(unresolved)],
    };
}

function normalizedVersion(value) {
    if (typeof value === "bigint") return value.toString();
    if (typeof value === "string" && value.length > 0) return value;
    if (typeof value === "number" && Number.isSafeInteger(value) && value >= 0) return value;
    throw new TypeError("import response is missing a valid version");
}

function completedMapping(response, pending) {
    const view = response?.tenantView ?? response?.tenant_view ?? response;
    const metadata = view?.metadata;
    const serverId = metadata?.id;
    if (typeof serverId !== "string" || serverId.length === 0) {
        throw new TypeError("import response is missing a server id");
    }
    if (typeof (view?.definitionJson ?? view?.definition_json) !== "string") {
        throw new TypeError("import response is missing the sanitized definition");
    }
    // The server response proves the durable copy exists. Retain only the
    // idempotency/address mapping after that point; keeping the full definition
    // here would create a second, otherwise-unerasable local copy.
    const {
        definitionJson: _definitionJson,
        secretOverrides: _secretOverrides,
        ...durable
    } = pending;
    return {
        ...durable,
        state: "complete",
        serverId,
        serverVersion: normalizedVersion(metadata?.version),
        serverName: typeof metadata?.name === "string" ? metadata.name : pending.name,
        serverTitle: typeof metadata?.title === "string" ? metadata.title : pending.title,
    };
}

function compactCompletedMapping(mapping) {
    if (
        mapping?.state !== "complete"
        || (
            !Object.hasOwn(mapping, "definitionJson")
            && !Object.hasOwn(mapping, "secretOverrides")
        )
    ) {
        return mapping;
    }
    const {
        definitionJson: _definitionJson,
        secretOverrides: _secretOverrides,
        ...compact
    } = mapping;
    return compact;
}

function resultForError(localId, error, code = "failed") {
    return { localId, status: code, error };
}

function normalizedErrorCode(error) {
    return String(error?.code || "").replace(/^CODE_/, "").toUpperCase();
}

function structuredErrorReasons(error) {
    const reasons = new Set();
    for (const detail of Array.isArray(error?.details) ? error.details : []) {
        if (!detail || typeof detail !== "object") continue;
        for (const candidate of [
            detail.reason,
            detail.code,
            detail.errorInfo?.reason,
            detail.error_info?.reason,
        ]) {
            if (typeof candidate === "string" && candidate) {
                reasons.add(candidate.toUpperCase());
            }
        }
    }
    return reasons;
}

function isHardTenantViewQuotaRejection(error) {
    const reasons = structuredErrorReasons(error);
    if (
        reasons.has("TENANT_VIEW_TENANT_QUOTA_EXCEEDED")
        || reasons.has("TENANT_VIEW_USER_QUOTA_EXCEEDED")
    ) {
        return true;
    }
    const message = typeof error?.serverMessage === "string"
        ? error.serverMessage
        : "";
    return /^(?:tenant|user) tenant view limit \d+ reached$/.test(message);
}

function isDeletedIdempotentImportResult(error) {
    const reasons = structuredErrorReasons(error);
    if (reasons.has("TENANT_VIEW_IDEMPOTENT_RESULT_GONE")) return true;
    return error?.serverMessage === "the idempotent create result was deleted";
}

function isPermanentImportRejection(error) {
    const status = Number(error?.status);
    const code = normalizedErrorCode(error);
    if (
        status === 429
        || code === "8"
        || code === "RESOURCE_EXHAUSTED"
    ) {
        return isHardTenantViewQuotaRejection(error);
    }
    if (
        status === 412
        || code === "9"
        || code === "FAILED_PRECONDITION"
    ) {
        return isDeletedIdempotentImportResult(error);
    }
    return [400, 409, 413, 422].includes(status)
        || [
            "3",
            "6",
            "ALREADY_EXISTS",
            "CLIENT_VALIDATION",
            "INVALID_ARGUMENT",
        ].includes(code);
}

function sameImportRequest(
    mapping,
    { definitionJson, formatVersion, kind, name, secretOverrides, title },
) {
    return mapping.kind === kind
        && mapping.name === name
        && mapping.title === title
        && mapping.formatVersion === formatVersion
        && mapping.definitionJson === definitionJson
        && JSON.stringify(mapping.secretOverrides || []) === JSON.stringify(secretOverrides);
}

function legacyApprovalKey(kind, localId) {
    return `${kind}:${localId}`;
}

function approvalsForKey(secretOverrideApprovals, key) {
    if (secretOverrideApprovals instanceof Map) {
        return secretOverrideApprovals.get(key);
    }
    return secretOverrideApprovals?.[key];
}

async function inspectLegacyDefinition({
    capabilities,
    cryptoImpl,
    definition,
    kind,
    localId,
    secretOverrideApprovals,
}) {
    const inventory = inventoryDefinitionSecrets(definition);
    if (inventory.total === 0) return { secretOverrides: [] };
    const approvalKey = legacyApprovalKey(kind, localId);
    const detectorVersion = String(capabilities?.secretDetectorVersion || "");
    const maxOverrides = Number(capabilities?.maxSecretFindingOverrides);
    if (
        !detectorVersion
        || inventory.truncated
        || !Number.isSafeInteger(maxOverrides)
        || maxOverrides < inventory.total
    ) {
        return {
            result: {
                localId,
                status: "approval_unavailable",
                approvalKey,
                error: new Error(
                    "Secret-like content requires review, but this server cannot accept the complete approval set.",
                ),
            },
        };
    }

    const contentDigest = await tenantViewSecretOverrideDigest(definition, cryptoImpl);
    const findings = inventory.findings.map((finding) => ({
        kind: finding.kind,
        jsonPointer: finding.json_pointer,
    }));
    const inspection = {
        findings,
        contentDigest,
        detectorVersion,
        totalFindingCount: inventory.total,
        findingsTruncated: inventory.truncated,
    };
    const approvals = approvalsForKey(secretOverrideApprovals, approvalKey);
    if (!Array.isArray(approvals)) {
        return {
            result: {
                localId,
                status: "approval_required",
                approvalKey,
                secretInspection: inspection,
            },
        };
    }
    const byFinding = new Map(approvals.map((approval) => [
        `${approval?.kind}\u0000${approval?.jsonPointer}`,
        approval,
    ]));
    const secretOverrides = [];
    for (const finding of findings) {
        const approval = byFinding.get(
            `${finding.kind}\u0000${finding.jsonPointer}`,
        );
        const justification = String(approval?.justification || "").trim();
        const justificationBytes = TEXT_ENCODER.encode(justification).byteLength;
        if (justificationBytes < 8 || justificationBytes > 500) {
            return {
                result: {
                    localId,
                    status: "approval_required",
                    approvalKey,
                    secretInspection: inspection,
                },
            };
        }
        secretOverrides.push({
            ...finding,
            contentDigest,
            detectorVersion,
            justification,
        });
    }
    return { secretOverrides };
}

function recordId(record) {
    return typeof record?.id === "string" && record.id.length > 0 ? record.id : null;
}

function hasCloudCreateJournal(tenantId, kind, localId, storage) {
    try {
        return Boolean(openTenantViewWriteJournal({
            tenantId,
            operation: "create",
            kind,
            sourceKey: localId,
        }, { storage }).read());
    } catch {
        // Malformed or unavailable durable state must fail closed: importing
        // through a different endpoint/request id could duplicate a committed
        // Create whose response was lost.
        return true;
    }
}

function responseName(record, kind) {
    const value = kind === "kernel" ? record?.command : record?.slug;
    return typeof value === "string" ? value : "";
}

function responseTitle(record, name) {
    return typeof record?.title === "string" && record.title.trim()
        ? record.title.trim()
        : name;
}

async function importRecord({
    api,
    createRequestId,
    definition,
    formatVersion,
    kind,
    localId,
    mappingIdentity,
    name,
    secretOverrides = [],
    storage,
    title,
    assertOwnerCurrent,
}) {
    const existing = readLegacyViewMapping(mappingIdentity, storage);
    if (existing.status === "ok" && existing.mapping.state === "complete") {
        const mapping = compactCompletedMapping(existing.mapping);
        try {
            if (mapping !== existing.mapping) {
                writeJson(storage, mappingStorageKey(mappingIdentity), mapping);
            }
        } catch (error) {
            return resultForError(localId, error, "mapping_unavailable");
        }
        return {
            localId,
            status: "already_imported",
            mapping,
        };
    }
    if (existing.status === "invalid" || existing.status === "unavailable") {
        return resultForError(localId, existing.error, "mapping_unavailable");
    }

    const definitionJson = JSON.stringify(definition);
    if (
        existing.status === "ok"
        && existing.mapping.state === "rejected"
        && sameImportRequest(existing.mapping, {
            definitionJson,
            formatVersion,
            kind,
            name,
            secretOverrides,
            title,
        })
    ) {
        return {
            localId,
            status: "rejected",
            error: new Error(existing.mapping.rejectionMessage),
            mapping: existing.mapping,
        };
    }

    let pending = existing.status === "ok" && existing.mapping.state === "pending"
        ? existing.mapping
        : null;
    if (!pending) {
        try {
            pending = {
                state: "pending",
                kind,
                localId,
                name,
                title,
                formatVersion,
                definitionJson,
                secretOverrides,
                createRequestId: createRequestId(),
            };
            writeJson(storage, mappingStorageKey(mappingIdentity), pending);
        } catch (error) {
            return resultForError(localId, error, "mapping_unavailable");
        }
    }

    let response;
    try {
        assertOwnerCurrent();
        response = await api.importTenantView({
            kind: kind === "kernel" ? KERNEL_KIND : CANVAS_KIND,
            name: pending.name,
            title: pending.title,
            formatVersion: pending.formatVersion,
            definitionJson: pending.definitionJson,
            createRequestId: pending.createRequestId,
            secretOverrides: pending.secretOverrides || [],
        });
    } catch (error) {
        if (error instanceof LegacyImportIdentityChangedError) throw error;
        if (!isPermanentImportRejection(error)) {
            return resultForError(localId, error);
        }
        const rejected = {
            ...pending,
            state: "rejected",
            rejectionCode: String(error?.code || ""),
            rejectionStatus: Number.isFinite(Number(error?.status))
                ? Number(error.status)
                : null,
            rejectionMessage: String(
                error?.serverMessage || error?.message || "import was rejected",
            ),
        };
        try {
            writeJson(storage, mappingStorageKey(mappingIdentity), rejected);
        } catch (storageError) {
            return resultForError(localId, storageError, "mapping_unavailable");
        }
        return {
            localId,
            status: "rejected",
            error,
            mapping: rejected,
        };
    }

    let mapping;
    try {
        mapping = completedMapping(response, pending);
    } catch (error) {
        return resultForError(localId, error, "mapping_unavailable");
    }
    try {
        writeJson(storage, mappingStorageKey(mappingIdentity), mapping);
    } catch (error) {
        return {
            localId,
            status: "committed",
            mapping,
            localDurabilityWarning:
                "The view was imported, but its local mapping could not be saved. Retry before removing the legacy source.",
        };
    }
    return { localId, status: "imported", mapping };
}

function statusRecords(result) {
    return result?.status === "ok" ? result.records : [];
}

function finalStatus(report) {
    const results = [...report.kernels, ...report.canvases];
    const failed = results.some(({ status }) =>
        !["imported", "already_imported"].includes(status));
    const sourceFailure = [report.kernelBucket, report.canvasBucket]
        .some(({ status }) => ["invalid", "unavailable"].includes(status));
    const pinFailure = ["invalid", "unavailable", "incomplete"].includes(report.pins.status);
    const cleanupFailure = [report.kernelCleanup, report.canvasCleanup]
        .some(({ status } = {}) => ["invalid", "unavailable"].includes(status));
    return failed || sourceFailure || pinFailure || cleanupFailure ? "partial" : "complete";
}

function drainImportedRecords(storage, bucketKey, results) {
    const importedIds = new Set(
        results
            .filter(({ status }) => ["imported", "already_imported"].includes(status))
            .map(({ localId }) => localId)
            .filter(Boolean),
    );
    if (importedIds.size === 0) return { status: "unchanged", removed: 0 };

    const source = readJson(storage, bucketKey);
    if (source.status === "absent") return { status: "absent", removed: 0 };
    if (source.status !== "ok") return source;
    if (!Array.isArray(source.value)) {
        return {
            status: "invalid",
            key: bucketKey,
            error: new TypeError("legacy source bucket must contain an array"),
        };
    }

    const remaining = source.value.filter((record) => !importedIds.has(recordId(record)));
    const removed = source.value.length - remaining.length;
    if (removed === 0) return { status: "unchanged", removed: 0 };
    try {
        if (remaining.length === 0) storage.removeItem(bucketKey);
        else writeJson(storage, bucketKey, remaining);
        return { status: "complete", removed };
    } catch (error) {
        return { status: "unavailable", key: bucketKey, error };
    }
}

export async function importLegacyTenantViews({
    tenantId,
    userId,
    bucketId = "default",
    api,
    storage = globalThis.localStorage,
    readKernelBucket = readRawKernelBucket,
    readCanvasBucket = readRawCanvasTemplateBucket,
    readPinBucket = (id) => readRawLegacyKernelPinMap(id, storage),
    createRequestId = () => createTenantViewRequestId(),
    bundledKernelIds = BUILTIN_KERNEL_IDS,
    cloudKernelIds = new Map(),
    capabilities = {},
    cryptoImpl = globalThis.crypto,
    secretOverrideApprovals = {},
    writePinMap = (map) => writeMigratedPinMap(tenantId, userId, map, storage),
} = {}) {
    requireIdentity(tenantId, "tenantId");
    requireIdentity(userId, "userId");
    const owner = requireBrowserStorageOwner(tenantId);
    const assertOwnerCurrent = () => {
        if (!browserStorageOwnerIsCurrent(owner)) {
            throw new LegacyImportIdentityChangedError(
                "Origin identity changed during legacy tenant view import",
            );
        }
    };
    if (!api || typeof api.importTenantView !== "function") {
        return {
            status: "unavailable",
            error: new TypeError("tenant view import is unavailable"),
            kernels: [],
            canvases: [],
            pins: { status: "preserved" },
        };
    }

    const kernelBucket = readKernelBucket(bucketId);
    const canvasBucket = readCanvasBucket(bucketId);
    const kernelBucketKey = kernelBucket?.key || `originChat.kernels.v1:${bucketId}`;
    const canvasBucketKey = canvasBucket?.key || `originChat.canvasTemplates.v1:${bucketId}`;
    const kernelRecords = statusRecords(kernelBucket);
    const canvasRecords = statusRecords(canvasBucket);
    const kernels = [];
    const canvases = [];

    const localKernelsByCommand = new Map();
    const kernelMappingByLocalId = new Map();
    for (const record of kernelRecords) {
        assertOwnerCurrent();
        const localId = recordId(record);
        let mapping = null;
        if (localId) {
            mapping = readLegacyViewMapping({
                tenantId,
                userId,
                bucketKey: kernelBucketKey,
                localRecordId: localId,
            }, storage);
            kernelMappingByLocalId.set(localId, mapping);
        }
        const command = mapping?.status === "ok"
            && mapping.mapping.state !== "rejected"
            ? mapping.mapping.name
            : record?.command;
        if (typeof command !== "string") continue;
        const matches = localKernelsByCommand.get(command) || [];
        matches.push(record);
        localKernelsByCommand.set(command, matches);
    }

    for (const record of kernelRecords) {
        const localId = recordId(record);
        if (!localId) {
            kernels.push(resultForError(null, new TypeError("legacy kernel is missing its local id")));
            continue;
        }
        if (hasCloudCreateJournal(tenantId, "kernel", localId, storage)) {
            kernels.push({ localId, status: "pending_cloud_create" });
            continue;
        }
        const mappingIdentity = {
            tenantId,
            userId,
            bucketKey: kernelBucketKey,
            localRecordId: localId,
        };
        const existing = kernelMappingByLocalId.get(localId);
        if (existing?.status === "ok" && existing.mapping.state === "complete") {
            kernels.push(await importRecord({
                api,
                createRequestId,
                definition: {},
                formatVersion: existing.mapping.formatVersion,
                kind: "kernel",
                localId,
                mappingIdentity,
                name: existing.mapping.name,
                storage,
                title: existing.mapping.title,
                assertOwnerCurrent,
            }));
            continue;
        }
        if (existing?.status === "invalid" || existing?.status === "unavailable") {
            kernels.push(resultForError(localId, existing.error, "mapping_unavailable"));
            continue;
        }
        if (existing?.status === "ok" && existing.mapping.state === "pending") {
            kernels.push(await importRecord({
                api,
                createRequestId,
                definition: {},
                formatVersion: existing.mapping.formatVersion,
                kind: "kernel",
                localId,
                mappingIdentity,
                name: existing.mapping.name,
                storage,
                title: existing.mapping.title,
                assertOwnerCurrent,
            }));
            continue;
        }
        const name = responseName(record, "kernel");
        if (!name) {
            kernels.push(resultForError(localId, new TypeError("legacy kernel is missing its command")));
            continue;
        }
        let definition;
        try {
            definition = cloneDefinition(record, "kernel");
        } catch (error) {
            kernels.push(resultForError(localId, error));
            continue;
        }
        let secretInspection;
        try {
            secretInspection = await inspectLegacyDefinition({
                capabilities,
                cryptoImpl,
                definition,
                kind: "kernel",
                localId,
                secretOverrideApprovals,
            });
        } catch (error) {
            kernels.push(resultForError(localId, error, "approval_unavailable"));
            continue;
        }
        if (secretInspection.result) {
            kernels.push(secretInspection.result);
            continue;
        }
        kernels.push(await importRecord({
            api,
            createRequestId,
            definition,
            formatVersion: 0,
            kind: "kernel",
            localId,
            mappingIdentity,
            name,
            secretOverrides: secretInspection.secretOverrides,
            storage,
            title: responseTitle(record, name),
            assertOwnerCurrent,
        }));
    }

    const importedKernelId = (command) => {
        const localMatches = localKernelsByCommand.get(command);
        if (localMatches?.length) {
            if (localMatches.length !== 1) return null;
            const localId = recordId(localMatches[0]);
            if (!localId) return null;
            const current = kernels.find((result) => result.localId === localId);
            if (
                ["imported", "already_imported", "committed"].includes(current?.status)
                && typeof current.mapping?.serverId === "string"
            ) {
                return current.mapping.serverId;
            }
            const mapping = readLegacyViewMapping({
                tenantId,
                userId,
                bucketKey: kernelBucketKey,
                localRecordId: localId,
            }, storage);
            return mapping.status === "ok" && mapping.mapping.state === "complete"
                ? mapping.mapping.serverId
                : null;
        }
        const bundledId = bundledKernelIds?.[command];
        if (typeof bundledId === "string" && bundledId.length > 0) return bundledId;
        const cloudId = cloudKernelIds instanceof Map ? cloudKernelIds.get(command) : null;
        return typeof cloudId === "string" && cloudId.length > 0 ? cloudId : null;
    };

    for (const record of canvasRecords) {
        assertOwnerCurrent();
        const localId = recordId(record);
        if (!localId) {
            canvases.push(resultForError(null, new TypeError("legacy canvas is missing its local id")));
            continue;
        }
        if (hasCloudCreateJournal(tenantId, "canvas", localId, storage)) {
            canvases.push({ localId, status: "pending_cloud_create" });
            continue;
        }
        const mappingIdentity = {
            tenantId,
            userId,
            bucketKey: canvasBucketKey,
            localRecordId: localId,
        };
        const existing = readLegacyViewMapping(mappingIdentity, storage);
        if (existing.status === "ok" && existing.mapping.state === "complete") {
            canvases.push(await importRecord({
                api,
                createRequestId,
                definition: {},
                formatVersion: existing.mapping.formatVersion,
                kind: "canvas",
                localId,
                mappingIdentity,
                name: existing.mapping.name,
                storage,
                title: existing.mapping.title,
                assertOwnerCurrent,
            }));
            continue;
        }
        if (existing.status === "invalid" || existing.status === "unavailable") {
            canvases.push(resultForError(localId, existing.error, "mapping_unavailable"));
            continue;
        }
        if (existing.status === "ok" && existing.mapping.state === "pending") {
            canvases.push(await importRecord({
                api,
                createRequestId,
                definition: {},
                formatVersion: existing.mapping.formatVersion,
                kind: "canvas",
                localId,
                mappingIdentity,
                name: existing.mapping.name,
                storage,
                title: existing.mapping.title,
                assertOwnerCurrent,
            }));
            continue;
        }
        const name = responseName(record, "canvas");
        if (!name) {
            canvases.push(resultForError(localId, new TypeError("legacy canvas is missing its slug")));
            continue;
        }
        let rewritten;
        try {
            rewritten = rewriteCanvasDependencies(record, importedKernelId);
        } catch (error) {
            canvases.push(resultForError(localId, error));
            continue;
        }
        if (rewritten.unresolved.length > 0) {
            canvases.push({
                localId,
                status: "waiting_for_dependencies",
                unresolved: rewritten.unresolved,
            });
            continue;
        }
        let secretInspection;
        try {
            secretInspection = await inspectLegacyDefinition({
                capabilities,
                cryptoImpl,
                definition: rewritten.definition,
                kind: "canvas",
                localId,
                secretOverrideApprovals,
            });
        } catch (error) {
            canvases.push(resultForError(localId, error, "approval_unavailable"));
            continue;
        }
        if (secretInspection.result) {
            canvases.push(secretInspection.result);
            continue;
        }
        canvases.push(await importRecord({
            api,
            createRequestId,
            definition: rewritten.definition,
            formatVersion: rewritten.formatVersion,
            kind: "canvas",
            localId,
            mappingIdentity,
            name,
            secretOverrides: secretInspection.secretOverrides,
            storage,
            title: responseTitle(record, name),
            assertOwnerCurrent,
        }));
    }

    assertOwnerCurrent();
    const pinBucket = readPinBucket(bucketId);
    let pins;
    if (pinBucket?.status === "absent") {
        pins = { status: "absent" };
    } else if (pinBucket?.status !== "ok") {
        pins = {
            status: pinBucket?.status || "unavailable",
            error: pinBucket?.error,
        };
    } else {
        pins = remapLegacyKernelPinMap(pinBucket.records, importedKernelId);
        if (pins.status === "complete") {
            try {
                const key = writePinMap(pins.map);
                pins = { ...pins, key };
            } catch (error) {
                pins = { status: "unavailable", map: null, unresolved: [], error };
            }
        }
    }

    const dependentMappingsAreDurable =
        ["ok", "absent"].includes(canvasBucket?.status)
        && canvases.every(({ status }) =>
            ["imported", "already_imported"].includes(status))
        && ["complete", "absent"].includes(pins.status);
    const report = {
        kernelBucket: kernelBucket || { status: "unavailable" },
        canvasBucket: canvasBucket || { status: "unavailable" },
        kernelCleanup: dependentMappingsAreDurable
            ? drainImportedRecords(storage, kernelBucketKey, kernels)
            : { status: "preserved", removed: 0 },
        canvasCleanup: drainImportedRecords(storage, canvasBucketKey, canvases),
        kernels,
        canvases,
        pins,
    };
    return { ...report, status: finalStatus(report) };
}
